import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { formatEatDate, formatEatTime } from '@wodi/domain';
import { Button, EmptyState, LoadingBlock, Notice, Section, Tag } from '@wodi/ui';
import { useState } from 'react';

import {
  appointmentKeys,
  patientAppointmentsQuery,
  recordReminderConsent,
} from '../api/appointments.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './appointments.module.css';
import { BookingDialog } from './booking-dialog.tsx';
import { KIND_LABELS, reminderText, statusTag } from './labels.ts';

// The patient's appointments on their record (appointments plan §6): upcoming first,
// then the last 90 days, with the SMS reminder answer and how to change it.

export function PatientAppointmentsSection({
  patient,
}: {
  patient: { id: string; displayName: string };
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const list = useQuery(patientAppointmentsQuery(patient.id));
  const [booking, setBooking] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const mayBook = session !== null && can(session, 'appointments.book');
  const consent = useMutation({
    mutationFn: (body: { decision: 'granted' | 'withdrawn'; reason?: string }) =>
      recordReminderConsent(patient.id, body),
    onSuccess: async (_d, body) => {
      await queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      setWithdrawing(false);
      toasts.show({
        title: body.decision === 'granted' ? 'SMS reminders on' : 'SMS reminders off',
      });
    },
  });

  const decision = list.data?.reminderConsent.decision ?? null;
  const smsLine =
    list.data === undefined
      ? ''
      : list.data.mobileLastDigits === null
        ? 'SMS reminders: no mobile number on file.'
        : decision === 'granted'
          ? `SMS reminders: yes, to the number ending ${list.data.mobileLastDigits}.`
          : decision === null
            ? 'SMS reminders: not asked yet.'
            : 'SMS reminders: the patient said no.';

  return (
    <Section
      title="Appointments"
      {...(mayBook
        ? {
            actions: (
              <Button
                dense
                isDisabled={!online}
                onPress={() => {
                  setBooking(true);
                }}
              >
                Book appointment
              </Button>
            ),
          }
        : {})}
    >
      {list.isPending ? <LoadingBlock label="Loading appointments" rows={2} /> : null}
      {list.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void list.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(list.error, "Appointments couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {list.isSuccess ? (
        <>
          <div className={styles.row}>
            <span className={styles.meta}>{smsLine}</span>
            {mayBook && online && list.data.mobileLastDigits !== null ? (
              decision === 'granted' ? (
                <Button
                  dense
                  variant="quiet"
                  onPress={() => {
                    consent.reset();
                    setWithdrawing(true);
                  }}
                >
                  Stop SMS reminders
                </Button>
              ) : (
                <Button
                  dense
                  variant="quiet"
                  isPending={consent.isPending}
                  pendingLabel="Saving…"
                  onPress={() => {
                    consent.mutate({ decision: 'granted' });
                  }}
                >
                  Patient agrees to SMS reminders
                </Button>
              )
            ) : null}
          </div>
          {consent.isError && !withdrawing ? (
            <Notice tone="error">{errorText(consent.error, "That couldn't be saved.")}</Notice>
          ) : null}
          {list.data.items.length === 0 ? (
            <EmptyState>No appointments in the last 90 days or ahead.</EmptyState>
          ) : (
            <ul className={styles.list}>
              {list.data.items.map((a) => {
                const tag = statusTag(a);
                return (
                  <li key={a.id} className={styles.listItem}>
                    <span>
                      <span className={styles.time}>
                        {`${formatEatDate(new Date(a.startsAt))} ${formatEatTime(new Date(a.startsAt))}`}
                      </span>
                      {` ${a.servicePoint.name}, ${KIND_LABELS[a.kind].toLowerCase()}`}
                      <span className={styles.meta}>{`  ${reminderText(a.reminder)}`}</span>
                    </span>
                    <Tag state={tag.state}>{tag.label}</Tag>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      ) : null}
      {booking ? (
        <BookingDialog
          mode={{ kind: 'book', patient }}
          isOpen
          onOpenChange={setBooking}
          onDone={(a) => {
            toasts.show({
              title: 'Appointment booked',
              description: `${formatEatDate(new Date(a.startsAt))} at ${formatEatTime(new Date(a.startsAt))}.`,
            });
            setBooking(false);
          }}
        />
      ) : null}
      <ReasonDialog
        title="Stop SMS reminders"
        isOpen={withdrawing}
        onOpenChange={setWithdrawing}
        confirmLabel="Stop SMS reminders"
        pendingLabel="Saving…"
        isPending={consent.isPending}
        error={consent.isError ? errorText(consent.error, "That couldn't be saved.") : null}
        onConfirm={(reason) => {
          consent.mutate({ decision: 'withdrawn', reason });
        }}
      />
    </Section>
  );
}
